import PropTypes from 'prop-types';
import { format } from 'date-fns';
import styled, { css } from 'styled-components';
import { RadioButton } from '@bwoty-web/ui-kit';
import MatchDate from './MatchDate';
import OpponentComp from './OpponentComp';
import locales from '../utils/locales';

export const TableRow = styled.tr`
  && {
    ${(props) => {
      if (props.active) {
        return css`
          td:last-of-type {
            border-bottom: 0;
            border-radius: 0;
          }
          /* Desktop only */
          @media (min-width: 768px) {
            background: var(--color-background-surface-tertiary);
            color: var(--color-white);
          }
        `;
      }
      if (props.unavailable) {
        return css`
          /* Desktop only */
          @media (min-width: 768px) {
            color: var(--color-grey-500);
            background: var(--color-background-disabled);
            label {
              cursor: default;
            }
          }
          /* Mobile only */
          @media (max-width: 767px) {
            td {
              background: var(--color-background-disabled);
            }
          }
        `;
      }
      if (!props.selected || !props.unavailable) {
        return css`
          /* Desktop only */
          @media (min-width: 768px) {
            &:hover:not(.no-fixtures) {
              background: var(--color-core-100);
              transition: background 0.25s ease;
              cursor: pointer;
            }
          }
        `;
      }
      return undefined;
    }}
  }
`;

export const TableData = styled.td`
  padding: var(--space-400) var(--space-300);
  vertical-align: middle;
  text-align: left;
  background: none;

  .sg-radio-button__label {
    width: 100%;
  }

  /* Mobile only */
  @media (max-width: 767px) {
    ${TableRow} & {
      background: ${(props) => (props.selected ? 'var(--color-dark-gray)' : '#fff')};
      color: ${(props) => (props.selected ? '#fff' : 'var(--color-dark-gray)')};
    }
  }
`;

const FixtureRow = ({ fixture, selected, handleSelected, labels, siteSettings }) => {
  const { siteId } = siteSettings;
  const { bookingTexts } = labels;
  const { base, full, opponentCode, unavailable } = fixture;

  function handleClick(e) {
    const selectedPackage = e.target.id;
    if (!unavailable) {
      handleSelected(fixture, selectedPackage);
    }
  }

  const active = selected.includes(opponentCode);

  const labelText = (packageType) => {
    if (packageType.status === 'in sale') {
      return `${format(new Date(packageType.from), locales[siteId].dateShort, {
        locale: locales[siteId].lang,
      })}-${format(new Date(packageType.to), locales[siteId].dateShort, {
        locale: locales[siteId].lang,
      })}`;
    }
    if (packageType.status === 'sold out') {
      return bookingTexts.soldout;
    }
    return '-';
  };

  return (
    <TableRow
      id={opponentCode}
      key={opponentCode}
      onClick={(e) => {
        handleClick(e);
      }}
      active={active}
      unavailable={unavailable}
      className={`list${active ? ' expanded' : ''}`}
    >
      <TableData>
        <MatchDate fixture={fixture} active={active} siteId={siteId} />
      </TableData>
      <TableData>
        <OpponentComp fixture={fixture} active={active} />
      </TableData>
      <TableData selected={selected === `${opponentCode}_base`}>
        {base.status === 'sold out' || unavailable ? (
          <RadioButton uniqueId={`${opponentCode}_base`} onChange={() => {}} disabled readOnly>
            {labelText(base)}
          </RadioButton>
        ) : (
          <RadioButton
            checked={selected === `${opponentCode}_base`}
            uniqueId={`${opponentCode}_base`}
            onChange={(e) => handleClick(e)}
          >
            {labelText(base)}
          </RadioButton>
        )}
      </TableData>
      <TableData selected={selected === `${opponentCode}_full`}>
        {full.status === 'sold out' || unavailable ? (
          <RadioButton uniqueId={`${opponentCode}_full`} onChange={() => {}} disabled readOnly>
            {labelText(full)}
          </RadioButton>
        ) : (
          <RadioButton
            checked={selected === `${opponentCode}_full`}
            uniqueId={`${opponentCode}_full`}
            onChange={(e) => handleClick(e)}
          >
            {labelText(full)}
          </RadioButton>
        )}
      </TableData>
    </TableRow>
  );
};

FixtureRow.propTypes = {
  fixture: PropTypes.shape().isRequired,
  labels: PropTypes.shape({
    bookingTexts: PropTypes.shape(),
  }),
  selected: PropTypes.string,
  handleSelected: PropTypes.func.isRequired,
  siteSettings: PropTypes.shape({
    siteId: PropTypes.number,
  }),
};

FixtureRow.defaultProps = {
  labels: null,
  selected: '',
  siteSettings: null,
};

export default FixtureRow;
